import React from 'react';
import CustomMedia from 'util/custom-media-query';
import FormDialogrenderer from './FormDialogRenderer';
import i18n from 'i18n';
import { css } from 'styled-components';
import {
  applyStyles,
  convertDoubleWidthNumber,
  convertKana,
  convertDoubleWidthAlpha,
  convertDoubleWidthSymbol,
  replaceHyphenAndSpaces,
  allowExactKeysOnly
} from 'util/helpers';
import Button from 'components/common/Button';
import TextField from 'components/common/TextField';
import SelectField from 'components/common/SelectField';
import {
  required,
  mustBeNumber,
  kanaPattern,
  emailPattern,
  composeValidators
} from 'validation/validators';
import FontStyles from 'constants/font-styles';
import { get } from 'lodash';

const handleBlurNumbers = (form, key, value) => {
  const { change } = form;
  const replaced = convertDoubleWidthNumber(value);
  const formatted = replaceHyphenAndSpaces(replaced);

  change(key, formatted);
};

const handleBlurKana = (form, value) => {
  const { change } = form;
  const replaced = convertKana(value);

  change('nameKana', replaced);
};

const handleBlurEmail = (form, value) => {
  const { change } = form;
  const replacedNumber = convertDoubleWidthNumber(value);
  const replacedAlpha = convertDoubleWidthAlpha(replacedNumber);
  const replaced = convertDoubleWidthSymbol(replacedAlpha);

  change('email', replaced);
};

const createSelectShipperOptions = orders => [
  {
    label: i18n.t('orderAggregators:common:order.select'),
    value: ''
  },
  ...orders.map((_, index) => ({
    label: `${i18n.t('orderAggregators:common:order.order')}${index +
      1}${i18n.t('orderAggregators:common:order.copyShipperValueLabel')}`,
    value: index
  }))
];

const copyShipperInfo = (form, index, orders) => {
  if (index === '') return;
  const { change } = form;
  const shipper = orders[+index].shipper;
  const customerKeys = ['name', 'nameKana', 'telephone'];
  const mappingShipperKeys = {
    name: 'shipperName',
    nameKana: 'shipperNameKana',
    telephone: 'shipperTelephone'
  };

  return customerKeys.map(key =>
    change(`${key}`, get(shipper, mappingShipperKeys[key]))
  );
};

const DialogBody = ({ form, orders }) => (
  <div className="customer">
    <div className="fields-container">
      <React.Fragment>
        <SelectField
          name="copyShipperValue"
          label={i18n.t('orderAggregators:common:order.copyShipperLabel')}
          options={createSelectShipperOptions(orders)}
          onChange={e => copyShipperInfo(form, e.target.value, orders)}
        />

        <TextField
          name="name"
          validate={value => composeValidators(required)(value)}
          label={i18n.t('orderAggregators:payment:customer.name')}
          labelNote={i18n.t('orderAggregators:payment:customer.nameLabelNote')}
          maxLength={30}
          placeholder={i18n.t(
            'orderAggregators:payment:customer.namePlaceholder'
          )}
          required={true}
          notes={[i18n.t('orderAggregators:payment:customer.nameNote1')]}
        />

        <TextField
          name="nameKana"
          validate={value => kanaPattern(value)}
          label={i18n.t('orderAggregators:payment:customer.nameKana')}
          maxLength={30}
          placeholder={i18n.t(
            'orderAggregators:payment:customer.nameKanaPlaceholder'
          )}
          notes={[i18n.t('orderAggregators:payment:customer.nameKanaNote1')]}
          onBlur={value => handleBlurKana(form, value)}
        />

        <TextField
          type="tel"
          name="telephone"
          validate={value => composeValidators(required, mustBeNumber)(value)}
          label={i18n.t('orderAggregators:payment:customer.telephone')}
          maxLength={12}
          placeholder={i18n.t(
            'orderAggregators:payment:customer.telephonePlaceholder'
          )}
          required={true}
          onBlur={value => handleBlurNumbers(form, 'telephone', value)}
          onKeyDown={event => allowExactKeysOnly(event, /[^-]/)}
        />

        <TextField
          name="email"
          validate={value => composeValidators(required, emailPattern)(value)}
          label={i18n.t('orderAggregators:payment:customer.email')}
          maxLength={255}
          placeholder={i18n.t(
            'orderAggregators:payment:customer.emailPlaceholder'
          )}
          onBlur={value => handleBlurEmail(form, value)}
          required={true}
        />
      </React.Fragment>
    </div>
  </div>
);

const DialogFooter = ({ handleSubmit, invalid }) => (
  <div className="footer-btn-wrapper">
    <Button
      buttonType="B"
      type="button"
      onClick={handleSubmit}
      disabled={invalid}
    >
      {i18n.t('common:save')}
    </Button>
  </div>
);

const CustomerDialog = ({ className, onSubmit, dialogRef, form }) => {
  const formValues = form.getState().values;

  return (
    <div className={className}>
      <FormDialogrenderer
        title={i18n.t('orderAggregators:common:order.customerEditDialogTitle')}
        innerRef={dialogRef}
        renderBody={({ form }) => (
          <DialogBody form={form} orders={formValues.orders} />
        )}
        renderFooter={props => <DialogFooter {...props} />}
        initialValues={formValues.customer}
        onSubmit={onSubmit}
        showCloseAlert={true}
      />
    </div>
  );
};

const commonStyle = css`
  .footer-btn-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  ${SelectField} {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;

    select {
      width: 148px;
    }

    label {
      ${FontStyles.text7};

      margin: 0 0 0 8px;
    }
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  .customer {
    padding: 0 32px 0;
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
`;

export default applyStyles(CustomerDialog)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/components/CustomerDialog.js